import { useState } from 'react';
import Box from '@mui/material/Box';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Avatar, Banner, Btn, Card, Cols, EmptyState, Field, KeyValues, Page, PageHeader, SoonBadge, TextInput, useToast, v } from '@infinix/ui';
import { accountApi, ApiError, useCrumbs, useSession } from '@infinix/shell';
import { roleName } from '@infinix/registry';

/**
 * My account: personal settings for every signed-in person (Google Account / Microsoft My account), on the one Nest
 * session. Identity, name and phone, organization, business units and roles come from Core through the account
 * backend. Parts whose Core API does not exist yet (editing the profile, two-step verification, active sessions,
 * notification and regional preferences) are "Coming soon", never stored in the browser as if they were real.
 */

export function ProfilePage() {
  useCrumbs([{ label: 'Profile' }]);
  const s = useSession();
  const bu = s.units.find((u) => u.id === s.buId);
  // Name and phone from Core's self-read (#1193); the page still shows who they are if Core can't answer.
  const me = useQuery({ queryKey: ['profile', s.email], queryFn: () => accountApi.profile(), staleTime: 60_000 });
  const fullName = [me.data?.firstName, me.data?.lastName].filter(Boolean).join(' ');
  const initials = fullName ? fullName.split(/\s+/).map((x) => x.charAt(0)).slice(0, 2).join('').toUpperCase() : (s.email ?? '?').charAt(0).toUpperCase();
  const phone = { k: 'Phone', v: me.isPending ? '…' : me.data?.phone ?? '—' };
  return (
    <Page>
      <PageHeader title="Profile" subtitle="Who you are in every Nest app" />
      <Cols cols="minmax(0,1.6fr) minmax(0,1fr)">
        <Card title="About you" action={<SoonBadge reason="editing your name, job title and phone needs Core's user profile API" />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', mb: '18px' }}>
            <Avatar initials={initials} size={52} />
            <Box>
              <Box sx={{ fontSize: 16, fontWeight: 600 }}>{fullName || s.email}</Box>
              <Box sx={{ fontSize: 13, color: v('caption') }}>{s.org?.name ?? 'Nest staff'}</Box>
            </Box>
          </Box>
          {me.isError && <Box sx={{ mb: '12px' }}><Banner tone="warning">Your name and phone couldn't be loaded just now.</Banner></Box>}
          <KeyValues rows={s.level === 'platform' ? [
            { k: 'Email', v: s.email ?? '—' },
            phone,
            { k: 'Access', v: 'Nest staff: the platform, in Ops' },
          ] : [
            { k: 'Email', v: s.email ?? '—' },
            phone,
            { k: 'Organization', v: s.org?.name ?? '—' },
            { k: 'Access', v: s.level === 'org' ? 'Every business unit in the organization' : (s.units[0]?.name ?? 'One business unit') },
            { k: 'Working in', v: bu ? bu.name : 'All business units' },
          ]} />
          <Box sx={{ mt: '12px', fontSize: 12.5, color: v('caption') }}>{s.org ? 'Managed by your organization: contact an administrator to change it.' : 'Managed by Nest platform admins.'}</Box>
        </Card>
        <Card title="Your access" subtitle={s.org ? `Roles in ${s.org.name}` : 'Nest staff roles'}>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {s.roles.map((r, i) => (
              <Box component="li" key={r} sx={{ py: '10px', borderTop: i ? `1px solid ${v('divider')}` : 0, pt: i ? '10px' : 0, fontSize: 13.5, fontWeight: 600 }}>
                {roleName(r)}
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: '12px', fontSize: 12.5, color: v('caption') }}>{s.org ? "Roles are assigned by your organization's admins." : 'Staff roles are assigned by Nest platform admins.'}</Box>
        </Card>
      </Cols>
    </Page>
  );
}

export function SecurityPage() {
  useCrumbs([{ label: 'Sign-in and security' }]);
  const s = useSession();
  const send = useMutation({ mutationFn: () => accountApi.forgotPassword(s.email ?? '') });
  return (
    <Page>
      <PageHeader title="Sign-in and security" subtitle="How you sign in to Nest" />
      <Cols cols="minmax(0,1fr) minmax(0,1fr)">
        <Card title="Password" subtitle="Change the password you sign in with">
          <ChangePassword />
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${v('border')}`, display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-start' }}>
            <Box sx={{ fontSize: 13, color: v('caption') }}>Forgot your current password?</Box>
            {send.isSuccess ? (
              <Banner tone="success">{send.data.message}</Banner>
            ) : (
              <>
                {send.isError && <Banner tone="danger">{send.error instanceof ApiError ? send.error.message : 'Something went wrong. Try again.'}</Banner>}
                <Btn kind="ghost" onClick={() => send.mutate()} disabled={send.isPending || !s.email}>{send.isPending ? 'Sending…' : 'Email me a reset link'}</Btn>
              </>
            )}
          </Box>
        </Card>
        <Card title="Two-step verification" action={<SoonBadge reason="needs Core's MFA enrolment API" />}>
          <EmptyState title="Coming soon" body="Two-step verification with an authenticator app or security key arrives with Core's sign-in upgrade." />
        </Card>
      </Cols>
      <Card title="Where you're signed in" action={<SoonBadge reason="needs Core's session list API" />}>
        <EmptyState title="Coming soon" body="The devices signed in to your Nest account, with sign-out per device." />
      </Card>
    </Page>
  );
}

export function NotificationsPage() {
  useCrumbs([{ label: 'Notifications' }]);
  return (
    <Page>
      <PageHeader title="Notifications" subtitle="What Nest tells you about, and how" />
      <Card><EmptyState title="Coming soon" body="Notification preferences need Core's notification service." /></Card>
    </Page>
  );
}

export function RegionalPage() {
  useCrumbs([{ label: 'Regional' }]);
  return (
    <Page>
      <PageHeader title="Regional" subtitle="Time zone, date format and first day of the week" />
      <Card><EmptyState title="Coming soon" body="Regional preferences need Core's user profile API." /></Card>
    </Page>
  );
}

/** Change one's own password: current, new (8 to 128 characters, different), confirm. Never trimmed or kept. */
function ChangePassword() {
  const toast = useToast();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [tried, setTried] = useState(false);
  const change = useMutation({
    mutationFn: () => accountApi.changePassword(form.current, form.next),
    onSuccess: () => { toast('Password changed'); setForm({ current: '', next: '', confirm: '' }); setTried(false); },
  });
  const set = (k: keyof typeof form) => (x: string) => setForm((f) => ({ ...f, [k]: x }));
  const problems = {
    current: !form.current ? 'Enter your current password' : undefined,
    next: form.next.length < 8 || form.next.length > 128 ? 'Use 8 to 128 characters' : form.next === form.current ? 'Choose a password different from your current one' : undefined,
    confirm: form.confirm !== form.next ? "The two new passwords don't match" : undefined,
  };
  const server = change.error instanceof ApiError ? change.error.fields : {};
  const err = (k: keyof typeof problems, serverKey: string) => (tried ? problems[k] : undefined) ?? server[serverKey];
  const submit = () => { setTried(true); if (!Object.values(problems).some(Boolean)) change.mutate(); };
  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); submit(); }} sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {change.isError && Object.keys(server).length === 0 && <Banner tone="danger">{change.error instanceof ApiError ? change.error.message : 'Something went wrong. Try again.'}</Banner>}
      <Field label="Current password" htmlFor="pw-current" error={err('current', 'currentPassword')}>
        <TextInput id="pw-current" type="password" autoComplete="current-password" value={form.current} onChange={set('current')} maxLength={256} invalid={Boolean(err('current', 'currentPassword'))} />
      </Field>
      <Field label="New password" htmlFor="pw-new" hint="8 to 128 characters" error={err('next', 'newPassword')}>
        <TextInput id="pw-new" type="password" autoComplete="new-password" value={form.next} onChange={set('next')} maxLength={128} invalid={Boolean(err('next', 'newPassword'))} />
      </Field>
      <Field label="Confirm new password" htmlFor="pw-confirm" error={tried ? problems.confirm : undefined}>
        <TextInput id="pw-confirm" type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} maxLength={128} invalid={Boolean(tried && problems.confirm)} />
      </Field>
      <Box><Btn kind="primary" type="submit" disabled={change.isPending}>{change.isPending ? 'Changing…' : 'Change password'}</Btn></Box>
    </Box>
  );
}
